import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import {
  type EmployeeProjectBrief,
  type EmployeeProjects,
  employeeProjectsQueryOptions,
} from '@/entities/employee'
import { ApiError } from '@/shared/api'
import { Avatar } from '@/shared/ui/avatar'
import { Breadcrumb } from '@/shared/ui/breadcrumb'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

// Страница сотрудника: его живые проекты в двух разрезах — где он ответственный
// и где просто в команде. Employee-only, как и реестр.
export const Route = createFileRoute('/_app/employees/$employeeId')({
  beforeLoad: ({ context }) => {
    if (context.session?.kind === 'customer') throw redirect({ to: '/projects' })
  },
  head: () => ({ meta: [{ title: 'Сотрудник · STIT Deal' }] }),
  component: EmployeePage,
})

function EmployeePage() {
  const { employeeId } = Route.useParams()
  const q = useQuery(employeeProjectsQueryOptions(employeeId))

  if (q.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 404) {
      return <StatusScreen title="Сотрудник не найден" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить сотрудника" message="Обновите страницу." />
  }

  return <EmployeeContent employee={q.data} />
}

function EmployeeContent({ employee }: { employee: EmployeeProjects }) {
  // Проект, где сотрудник и ведёт, и состоит в команде, приходит в обоих
  // разрезах — помечаем такой в «Команде», чтобы не читался дублем.
  const leads = new Set(employee.responsible.map((p) => p.id))

  return (
    <div className="p-4 md:p-6">
      <Breadcrumb
        className="mb-4"
        items={[{ label: 'Сотрудники', to: '/employees' }, { label: employee.full_name }]}
      />

      <div className="mb-6 flex items-center gap-4">
        <Avatar name={employee.full_name} size="lg" />
        <div className="min-w-0">
          <PageHeader title={employee.full_name} />
          {employee.email && (
            <div className="-mt-4 truncate text-text-tertiary text-sm">{employee.email}</div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <RoleSection title="Ответственный" projects={employee.responsible} />
        <RoleSection title="В команде" projects={employee.team} alsoLeads={leads} />
      </div>
    </div>
  )
}

function RoleSection({
  title,
  projects,
  alsoLeads,
}: {
  title: string
  projects: EmployeeProjectBrief[]
  /** Проекты, где сотрудник ещё и ответственный (пометка в разрезе «В команде»). */
  alsoLeads?: Set<string>
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="t-h3 text-text-primary">{title}</h2>
        <span className="mono-num text-sm text-text-tertiary">{projects.length}</span>
      </div>
      {projects.length === 0 ? (
        <p className="text-sm text-text-tertiary">Проектов нет.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {projects.map((project) => (
            <li key={project.id}>
              <Link
                to="/$company/$project"
                params={{ company: project.company_slug, project: project.slug }}
                className="flex items-center gap-3 rounded-card bg-bg-surface px-4 py-3 ring-1 ring-border-subtle ring-inset transition-colors hover:bg-bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {project.title}
                </span>
                <span className="min-w-0 truncate font-mono text-text-tertiary text-xs">
                  {project.company_name}
                </span>
                {alsoLeads?.has(project.id) && (
                  <span className="label shrink-0" title="Он же ответственный по проекту">
                    отв.
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
